// Permanent right-rail chat panel for the Strudel coding agent.
//
// UX design principles (see the plan in plans/ for the full brief):
//   1. breathing — the streaming dot pulses on the beat, tied to cps
//   2. instrument voices — each tool has a glyph + accent color, and
//      applied writes emit a one-frame flash in that color
//   3. consequence — applied AST writes flash the changed range in
//      the editor (see changeFlashExtension) and scroll it into view
//   4. keep/kill — decision buttons reframed as musical, not clerical
//   5. improvisation — slash palette for prompt seeds, selection
//      activity sparkline, Cmd-Enter to re-run the last prompt

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { EditRecipe } from '../blocks/editContext';
import type { Song } from '../blocks/types';
import type { AgentBridge } from '../agent/bridge';
import {
  sendTurn,
  type ChatMessage,
  type PendingToolCall,
  type ToolCallTrace,
  type UsageSummary,
} from '../agent/client';
import { costOf, formatCost } from '../agent/cost';
import { routeIntent, type RouteProfile } from '../agent/router';
import type { SystemProfile } from '../agent/systemPrompt';
import { toolStyleFor } from '../agent/toolStyle';
import {
  DEFAULT_MODEL,
  MODEL_OPTIONS,
  capabilitiesFor,
  getAnthropicKey,
  getEffort,
  getModel,
  setEffort as persistEffort,
  setModel as persistModel,
  type EffortLevel,
  type ModelId,
} from '../agent/storage';
import type { EditorSelection } from './SourcePane';

const AUTONOMOUS_KEY = 'strudel-harness:agent-autonomous:v1';
const ROUTING_KEY = 'strudel-harness:agent-routing:v1';

type RoutingMode = 'auto' | 'manual';

function loadAutonomousPref(): boolean {
  try {
    return localStorage.getItem(AUTONOMOUS_KEY) === '1';
  } catch {
    return false;
  }
}

function saveAutonomousPref(v: boolean): void {
  try {
    localStorage.setItem(AUTONOMOUS_KEY, v ? '1' : '0');
  } catch {
    // private-mode / quota — in-memory state still fine.
  }
}

function loadRoutingPref(): RoutingMode {
  try {
    return localStorage.getItem(ROUTING_KEY) === 'manual' ? 'manual' : 'auto';
  } catch {
    return 'auto';
  }
}

function saveRoutingPref(mode: RoutingMode): void {
  try {
    localStorage.setItem(ROUTING_KEY, mode);
  } catch {
    // ignore
  }
}

// ─── Slash-palette prompt seeds ────────────────────────────────────
// Typing `/` in the input opens a popover of canned prompts. Each
// seed is a full user-message string that replaces the `/` prefix and
// sends immediately. Deliberately musical and imperative rather than
// polite.

type SlashSeed = {
  label: string;
  prompt: string;
};

const SLASH_SEEDS: SlashSeed[] = [
  {
    label: 'swap bass',
    prompt: 'Swap the current bass line for something in the same key.',
  },
  {
    label: 'double time',
    prompt: 'Double the tempo of the song.',
  },
  {
    label: 'half it',
    prompt: 'Halve the tempo — slow everything to half-time feel.',
  },
  {
    label: 'solo last voice',
    prompt: 'Solo the voice that was most recently added.',
  },
  {
    label: 'brighten',
    prompt:
      'Brighten the mix — raise the lpf cutoff on any voice whose filter is below 1 kHz.',
  },
  {
    label: 'muddier',
    prompt:
      'Make the song muddier — lower the lpf on every voice by around 30%.',
  },
  {
    label: 'break it down',
    prompt:
      'Arrange a breakdown: first 16 cycles full mix, next 16 cycles hats + bass only, then return to full.',
  },
  {
    label: 'reverb wash',
    prompt:
      'Add a tasteful room on the pad/lead voices — enough to feel space, not a swimming pool.',
  },
];

/** A prompt pushed into the panel from outside (e.g. the onboarding
 *  tour's "meet the agent" beat). The id makes re-seeding the same
 *  text distinguishable — each new id sends once. */
export type SeededPrompt = { id: number; prompt: string };

type Props = {
  onOpenSettings: () => void;
  bridge: AgentBridge;
  /** Current CodeMirror selection. Null when nothing is highlighted. */
  selection: EditorSelection | null;
  /** Optional externally-seeded prompt; sent as a normal user turn. */
  seededPrompt?: SeededPrompt | null;
};

type Turn = {
  id: string;
  user: string;
  assistant: string;
  toolCalls: ToolCallTrace[];
  pending: PendingToolCall[];
  snapshot: Song | null;
  hasAppliedWrite: boolean;
  reverted: boolean;
  usage?: UsageSummary;
  model: ModelId;
  /** When auto-routing is on, the profile the router chose for this
   *  turn. Surfaced in the UI so the user understands why a turn ran
   *  on Haiku/Sonnet/Opus and at which effort. */
  route?: RouteProfile;
  /** Milliseconds per beat at the moment the turn started. Drives the
   *  streaming indicator's pulse rate so the panel breathes with the
   *  song. Null when the engine isn't playing. */
  beatMs: number | null;
  status: 'streaming' | 'done' | 'error' | 'aborted';
  error?: string;
};

type DecisionResolver = (decision: 'apply' | 'skip') => void;

export function ChatPanel({
  onOpenSettings,
  bridge,
  selection,
  seededPrompt,
}: Props) {
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [hasKey, setHasKey] = useState<boolean>(false);
  const [includeSelection, setIncludeSelection] = useState<boolean>(true);
  useEffect(() => {
    if (selection) setIncludeSelection(true);
  }, [selection]);
  const [autonomous, setAutonomousState] = useState<boolean>(loadAutonomousPref);
  const [routing, setRoutingState] = useState<RoutingMode>(loadRoutingPref);
  const [model, setModelState] = useState<ModelId>(() => {
    try {
      return getModel();
    } catch {
      return DEFAULT_MODEL;
    }
  });
  const [effort, setEffortState] = useState<EffortLevel | null>(() => {
    try {
      return getEffort(getModel());
    } catch {
      return null;
    }
  });

  const setAutonomous = useCallback((v: boolean) => {
    setAutonomousState(v);
    saveAutonomousPref(v);
  }, []);

  const setRouting = useCallback((mode: RoutingMode) => {
    setRoutingState(mode);
    saveRoutingPref(mode);
  }, []);

  const setModel = useCallback((id: ModelId) => {
    setModelState(id);
    persistModel(id);
    setEffortState(getEffort(id));
  }, []);

  const setEffort = useCallback((next: EffortLevel) => {
    setEffortState(next);
    persistEffort(next);
  }, []);

  const clearChat = useCallback(() => {
    for (const [, resolve] of decisionsRef.current) resolve('skip');
    decisionsRef.current.clear();
    lastPromptRef.current = '';
    setTurns([]);
  }, []);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const decisionsRef = useRef<Map<string, DecisionResolver>>(new Map());
  // Last user prompt — Cmd-Enter re-runs it verbatim against the
  // current state. Updated on every successful send.
  const lastPromptRef = useRef<string>('');

  const resolveDecision = useCallback(
    (turnId: string, useId: string, decision: 'apply' | 'skip') => {
      const resolve = decisionsRef.current.get(useId);
      if (!resolve) return;
      decisionsRef.current.delete(useId);
      setTurns((prev) =>
        prev.map((t) =>
          t.id === turnId
            ? { ...t, pending: t.pending.filter((p) => p.id !== useId) }
            : t,
        ),
      );
      resolve(decision);
    },
    [],
  );

  useEffect(() => {
    setHasKey(getAnthropicKey() !== null);
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!streaming) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        abortRef.current?.abort();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [streaming]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [turns]);

  const send = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed) return;
      const key = getAnthropicKey();
      if (!key) {
        setHasKey(false);
        return;
      }

      const attachedSelection =
        includeSelection && selection ? selection : null;
      const turnId = crypto.randomUUID();
      const snapshot = safeGetSong(bridge);
      // Lock the beat duration at turn start — tempo rarely changes
      // inside a single turn, and fixing it avoids jitter on the
      // pulse animation from ref polling.
      const cps = bridge.getCps();
      const beatMs = cps && cps > 0 ? 250 / cps : null;

      const newTurn: Turn = {
        id: turnId,
        user: trimmed,
        assistant: '',
        toolCalls: [],
        pending: [],
        snapshot,
        hasAppliedWrite: false,
        reverted: false,
        model,
        beatMs,
        status: 'streaming',
      };
      setTurns((prev) => [...prev, newTurn]);
      setInput('');
      setStreaming(true);
      lastPromptRef.current = trimmed;

      const controller = new AbortController();
      abortRef.current = controller;

      const history: ChatMessage[] = turns
        .filter((t) => t.status === 'done')
        .flatMap((t) => [
          { role: 'user' as const, content: t.user },
          { role: 'assistant' as const, content: t.assistant },
        ]);

      const userMessage = composeMessage(trimmed, attachedSelection);

      // When auto-routing is on, classify intent on Haiku and let the
      // router pick model/effort/system-profile/budget. Cost is ~$0.0005
      // per turn for the classification — cheap insurance against a
      // small "what's playing?" running on Opus with thinking on.
      // Manual mode preserves today's behaviour: trust the UI selectors.
      let effectiveModel: ModelId = model;
      let effectiveEffort: EffortLevel | null = effort;
      let effectiveProfile: SystemProfile = 'compose';
      let effectiveToolBudget: number | undefined;

      if (routing === 'auto') {
        try {
          const route = await routeIntent({
            apiKey: key,
            userMessage,
            history,
            signal: controller.signal,
          });
          effectiveModel = route.model;
          effectiveEffort = route.effort;
          effectiveProfile = route.systemProfile;
          effectiveToolBudget = route.toolBudget;
          setTurns((prev) =>
            prev.map((t) =>
              t.id === turnId
                ? { ...t, model: route.model, route }
                : t,
            ),
          );
        } catch (e) {
          if ((e as Error).name === 'AbortError') {
            setTurns((prev) =>
              prev.map((t) =>
                t.id === turnId ? { ...t, status: 'aborted' } : t,
              ),
            );
            setStreaming(false);
            abortRef.current = null;
            return;
          }
          // Non-abort routing failure (network, malformed response):
          // fall through to whatever the UI selectors say. The Haiku
          // call is best-effort, not load-bearing.
        }
      }

      await sendTurn({
        apiKey: key,
        model: effectiveModel,
        effort: effectiveEffort,
        systemProfile: effectiveProfile,
        toolBudget: effectiveToolBudget,
        history,
        userMessage,
        bridge,
        signal: controller.signal,
        handlers: {
          onWriteDecision: autonomous
            ? undefined
            : (pending) => {
                setTurns((prev) =>
                  prev.map((t) =>
                    t.id === turnId
                      ? { ...t, pending: [...t.pending, pending] }
                      : t,
                  ),
                );
                return new Promise((resolve) => {
                  decisionsRef.current.set(pending.id, resolve);
                });
              },
          onTextDelta: (delta) => {
            setTurns((prev) =>
              prev.map((t) =>
                t.id === turnId
                  ? { ...t, assistant: t.assistant + delta }
                  : t,
              ),
            );
          },
          onToolCall: (trace) => {
            setTurns((prev) =>
              prev.map((t) =>
                t.id === turnId
                  ? {
                      ...t,
                      toolCalls: [...t.toolCalls, trace],
                      hasAppliedWrite:
                        t.hasAppliedWrite ||
                        (trace.status === 'applied' && trace.mutatesSong),
                    }
                  : t,
              ),
            );
          },
          onComplete: () => {
            setTurns((prev) =>
              prev.map((t) =>
                t.id === turnId ? { ...t, status: 'done' } : t,
              ),
            );
          },
          onUsage: (u) => {
            setTurns((prev) =>
              prev.map((t) => (t.id === turnId ? { ...t, usage: u } : t)),
            );
          },
          onError: (err) => {
            setTurns((prev) =>
              prev.map((t) => {
                if (t.id !== turnId) return t;
                for (const p of t.pending) {
                  const r = decisionsRef.current.get(p.id);
                  if (r) {
                    decisionsRef.current.delete(p.id);
                    r('skip');
                  }
                }
                return {
                  ...t,
                  pending: [],
                  status: err.name === 'AbortError' ? 'aborted' : 'error',
                  error: err.message,
                };
              }),
            );
          },
        },
      });

      setStreaming(false);
      abortRef.current = null;
    },
    [
      bridge,
      turns,
      autonomous,
      routing,
      model,
      effort,
      selection,
      includeSelection,
    ],
  );

  // Externally-seeded prompts run through the normal send() path so
  // they get history, routing, and the key-missing state for free.
  // Each seed id fires once; a seed that arrives mid-stream waits for
  // the turn to finish (streaming is a dep) rather than interleaving.
  const consumedSeedRef = useRef(0);
  useEffect(() => {
    if (!seededPrompt || seededPrompt.id === consumedSeedRef.current) return;
    if (streaming) return;
    consumedSeedRef.current = seededPrompt.id;
    void send(seededPrompt.prompt);
  }, [seededPrompt, streaming, send]);

  const revertTurn = useCallback(
    (turnId: string) => {
      const turn = turns.find((t) => t.id === turnId);
      if (!turn || !turn.snapshot || turn.reverted) return;
      const snapshot = turn.snapshot;
      const recipe: EditRecipe = (draft) => {
        draft.statements = cloneStatements(snapshot.statements);
      };
      const result = bridge.applyRecipe(recipe);
      if (!result.ok) return;
      setTurns((prev) =>
        prev.map((t) => (t.id === turnId ? { ...t, reverted: true } : t)),
      );
    },
    [turns, bridge],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (streaming) return;
      void send(input);
    },
    [send, input, streaming],
  );

  // ─── Slash palette state ──────────────────────────────────────────
  const slashQuery = input.startsWith('/') ? input.slice(1).trim() : null;
  const slashMatches = useMemo<SlashSeed[]>(() => {
    if (slashQuery === null) return [];
    if (slashQuery.length === 0) return SLASH_SEEDS;
    const q = slashQuery.toLowerCase();
    return SLASH_SEEDS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.prompt.toLowerCase().includes(q),
    );
  }, [slashQuery]);
  const [slashCursor, setSlashCursor] = useState(0);
  useEffect(() => {
    setSlashCursor(0);
  }, [slashQuery]);

  const acceptSlash = useCallback(
    (seed: SlashSeed) => {
      setInput('');
      lastPromptRef.current = seed.prompt;
      void send(seed.prompt);
    },
    [send],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Cmd/Ctrl-Enter: re-run the most recent prompt against the
      // current state — improvisation is repetition with variation.
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (streaming) return;
        const last = lastPromptRef.current;
        if (last) void send(last);
        return;
      }
      // Slash palette navigation.
      if (slashQuery !== null && slashMatches.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSlashCursor((c) => Math.min(slashMatches.length - 1, c + 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSlashCursor((c) => Math.max(0, c - 1));
          return;
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          const seed = slashMatches[slashCursor];
          if (seed) setInput(seed.prompt);
          return;
        }
        if (e.key === 'Escape') {
          // Dismiss the palette without losing the rest of the input.
          // Clear any leading slash segment; preserve anything after
          // the first whitespace (there usually isn't any, but be
          // defensive).
          e.preventDefault();
          setInput('');
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const seed = slashMatches[slashCursor];
          if (seed) acceptSlash(seed);
          return;
        }
      }
      // Normal Enter: send.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (streaming) return;
        void send(input);
      }
    },
    [
      send,
      input,
      streaming,
      slashQuery,
      slashMatches,
      slashCursor,
      acceptSlash,
    ],
  );

  return (
    <aside
      role="complementary"
      aria-label="Strudel coding agent"
      className="h-full w-full flex flex-col border-l border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950 min-h-0"
    >
      <PanelHeader
        streaming={streaming}
        autonomous={autonomous}
        onToggleAutonomous={() => setAutonomous(!autonomous)}
        onClear={clearChat}
        canClear={turns.length > 0 && !streaming}
        beatMs={
          turns.length > 0 ? turns[turns.length - 1]!.beatMs : null
        }
      />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-5"
      >
        {!hasKey ? (
          <KeyMissingState onOpenSettings={onOpenSettings} />
        ) : turns.length === 0 ? (
          <EmptyState />
        ) : (
          turns.map((turn, idx) => (
            <TurnBlock
              key={turn.id}
              turn={turn}
              isLast={idx === turns.length - 1}
              onDecision={(useId, d) => resolveDecision(turn.id, useId, d)}
              onRevert={() => revertTurn(turn.id)}
            />
          ))
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="shrink-0 border-t border-zinc-900 bg-zinc-950/70 p-3 relative"
      >
        {selection && (
          <SelectionChip
            selection={selection}
            included={includeSelection}
            onToggle={() => setIncludeSelection((v) => !v)}
            bridge={bridge}
          />
        )}
        {slashQuery !== null && slashMatches.length > 0 && (
          <SlashPalette
            matches={slashMatches}
            cursor={slashCursor}
            onHover={setSlashCursor}
            onAccept={acceptSlash}
          />
        )}
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!hasKey || streaming}
            placeholder={
              !hasKey
                ? 'Set your Anthropic API key in Settings to start…'
                : streaming
                  ? 'Streaming — Esc to cancel'
                  : 'Ask, or type / for prompt seeds. ⌘↵ repeats the last prompt.'
            }
            rows={3}
            className="w-full resize-none bg-zinc-900/80 border border-zinc-800 focus:border-yellow-300/50 focus:bg-zinc-900 focus:outline-none rounded text-[12.5px] text-zinc-100 placeholder:text-zinc-600 px-3 py-2 font-body disabled:opacity-60"
          />
          {streaming && (
            <button
              type="button"
              onClick={() => abortRef.current?.abort()}
              className="absolute right-2 bottom-2 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-display rounded-sm border border-rose-400/40 text-rose-300 bg-zinc-900 hover:bg-rose-400/10"
            >
              Cancel
            </button>
          )}
        </div>
        <PanelFooter
          routing={routing}
          onRoutingChange={setRouting}
          model={model}
          effort={effort}
          onModelChange={setModel}
          onEffortChange={setEffort}
          disabled={streaming}
        />
      </form>
    </aside>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function PanelHeader({
  streaming,
  autonomous,
  onToggleAutonomous,
  onClear,
  canClear,
  beatMs,
}: {
  streaming: boolean;
  autonomous: boolean;
  onToggleAutonomous: () => void;
  onClear: () => void;
  canClear: boolean;
  beatMs: number | null;
}) {
  // Pulse rate: when streaming and engine is playing, pulse on the
  // beat. Otherwise fall back to a slow ambient breath. Under 80ms is
  // strobe territory; clamp for safety.
  const pulseMs =
    streaming && beatMs != null ? Math.max(120, beatMs) : 1200;
  return (
    <header className="relative px-4 pt-4 pb-3 border-b border-zinc-900 bg-gradient-to-b from-zinc-900/50 to-transparent">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          style={{ animationDuration: `${pulseMs}ms` }}
          className={
            'h-2 w-2 rounded-full ' +
            (streaming
              ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)] animate-pulse'
              : 'bg-yellow-300 shadow-[0_0_12px_rgba(253,224,71,0.8)]')
          }
        />
        <h2 className="text-yellow-300 text-[11px] tracking-[0.4em] uppercase font-display leading-none">
          Coding agent
        </h2>
        <button
          type="button"
          onClick={onClear}
          disabled={!canClear}
          title="Clear the chat history and reset the agent's context"
          className="ml-auto px-2 py-0.5 text-[9px] tracking-[0.22em] uppercase font-display rounded-sm border border-zinc-800 bg-zinc-900 text-zinc-500 hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-800 disabled:hover:text-zinc-500"
        >
          clear
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-[10.5px] text-zinc-500 font-body leading-[1.45] flex-1">
          {autonomous
            ? 'Autonomous mode — edits apply immediately. Revert the last turn to undo its changes.'
            : 'Guided mode — keep or kill each edit. Cmd-Z rewinds kept edits.'}
        </p>
        <AutonomousToggle
          autonomous={autonomous}
          onToggle={onToggleAutonomous}
        />
      </div>
    </header>
  );
}

function AutonomousToggle({
  autonomous,
  onToggle,
}: {
  autonomous: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={autonomous}
      aria-label={
        autonomous ? 'Disable autonomous mode' : 'Enable autonomous mode'
      }
      onClick={onToggle}
      className={
        'shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[9px] tracking-[0.22em] uppercase font-display transition-colors ' +
        (autonomous
          ? 'border-emerald-400/50 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20'
          : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-yellow-300/40 hover:text-yellow-300')
      }
    >
      <span
        aria-hidden="true"
        className={
          'h-1 w-1 rounded-full ' +
          (autonomous ? 'bg-emerald-400' : 'bg-zinc-600')
        }
      />
      {autonomous ? 'auto' : 'guided'}
    </button>
  );
}

function PanelFooter({
  routing,
  onRoutingChange,
  model,
  effort,
  onModelChange,
  onEffortChange,
  disabled,
}: {
  routing: RoutingMode;
  onRoutingChange: (mode: RoutingMode) => void;
  model: ModelId;
  effort: EffortLevel | null;
  onModelChange: (id: ModelId) => void;
  onEffortChange: (next: EffortLevel) => void;
  disabled: boolean;
}) {
  const selected = MODEL_OPTIONS.find((m) => m.id === model);
  const caps = capabilitiesFor(model);
  const isAuto = routing === 'auto';
  return (
    <footer className="mt-2 flex items-center justify-between gap-3 text-[9px] tracking-[0.25em] uppercase font-display text-zinc-600">
      <span className="flex items-center gap-2">
        <button
          type="button"
          role="switch"
          aria-checked={isAuto}
          aria-label={
            isAuto
              ? 'Switch to manual model selection'
              : 'Switch to auto routing'
          }
          onClick={() => onRoutingChange(isAuto ? 'manual' : 'auto')}
          disabled={disabled}
          title={
            isAuto
              ? 'Auto: a Haiku classifier picks model + effort per turn so cheap requests run cheap.'
              : 'Manual: turns use the model and effort you pick below.'
          }
          className={
            'flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[9px] tracking-[0.22em] uppercase font-display transition-colors disabled:opacity-50 ' +
            (isAuto
              ? 'border-yellow-300/40 bg-yellow-300/10 text-yellow-300 hover:bg-yellow-300/20'
              : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-yellow-300/40 hover:text-yellow-300')
          }
        >
          <span
            aria-hidden="true"
            className={
              'h-1 w-1 rounded-full ' +
              (isAuto ? 'bg-yellow-300' : 'bg-zinc-600')
            }
          />
          {isAuto ? 'auto' : 'manual'}
        </button>
        <label
          className={
            'flex items-center gap-1.5 ' +
            (isAuto ? 'opacity-40' : 'cursor-pointer')
          }
          title={
            isAuto
              ? 'Disabled while auto routing is on.'
              : (selected?.hint ?? '')
          }
        >
          <span className="text-zinc-700">model</span>
          <Dropdown
            ariaLabel="Agent model"
            value={model}
            disabled={disabled || isAuto}
            onChange={(v) => onModelChange(v as ModelId)}
            options={MODEL_OPTIONS.map((o) => ({
              value: o.id,
              label: o.label,
            }))}
          />
        </label>
        {caps.efforts.length > 0 && effort && (
          <label
            className={
              'flex items-center gap-1.5 ' +
              (isAuto ? 'opacity-40' : 'cursor-pointer')
            }
            title={
              isAuto
                ? 'Disabled while auto routing is on.'
                : 'How much the model thinks per turn. Higher = better reasoning, more tokens.'
            }
          >
            <span className="text-zinc-700">effort</span>
            <Dropdown
              ariaLabel="Agent effort"
              value={effort}
              disabled={disabled || isAuto}
              onChange={(v) => onEffortChange(v as EffortLevel)}
              options={caps.efforts.map((e) => ({ value: e, label: e }))}
            />
          </label>
        )}
      </span>
      <span className="flex items-center gap-2">
        <kbd className="font-body normal-case tracking-normal text-[10px] text-zinc-500">
          ↵
        </kbd>
        <span>send</span>
        <span className="text-zinc-800">·</span>
        <kbd className="font-body normal-case tracking-normal text-[10px] text-zinc-500">
          ⌘↵
        </kbd>
        <span>again</span>
      </span>
    </footer>
  );
}

function SlashPalette({
  matches,
  cursor,
  onHover,
  onAccept,
}: {
  matches: SlashSeed[];
  cursor: number;
  onHover: (idx: number) => void;
  onAccept: (seed: SlashSeed) => void;
}) {
  return (
    <div
      role="listbox"
      aria-label="Prompt seeds"
      className="mb-2 rounded-sm border border-yellow-300/30 bg-zinc-950/95 overflow-hidden shadow-[0_-6px_24px_-6px_rgba(0,0,0,0.6)]"
    >
      <div className="px-2.5 py-1 text-[8.5px] tracking-[0.3em] uppercase font-display text-zinc-600 border-b border-zinc-900 flex items-center gap-2">
        <span className="h-1 w-1 rounded-full bg-yellow-300" />
        prompt seeds
        <span className="ml-auto text-zinc-700">↑↓ move · ↵ send · ⇥ edit</span>
      </div>
      <ul className="max-h-[40vh] overflow-y-auto scrollbar-thin">
        {matches.map((seed, idx) => (
          <li
            key={seed.label}
            role="option"
            aria-selected={idx === cursor}
            onMouseEnter={() => onHover(idx)}
            onClick={() => onAccept(seed)}
            className={
              'px-2.5 py-1.5 cursor-pointer border-l-2 flex items-baseline gap-2.5 ' +
              (idx === cursor
                ? 'bg-yellow-300/[0.08] border-yellow-300'
                : 'border-transparent hover:bg-zinc-900/60')
            }
          >
            <span
              className={
                'shrink-0 font-display tracking-[0.22em] uppercase text-[10px] ' +
                (idx === cursor ? 'text-yellow-300' : 'text-zinc-400')
              }
            >
              /{seed.label}
            </span>
            <span className="font-body text-[11px] text-zinc-500 leading-tight truncate">
              {seed.prompt}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SelectionChip({
  selection,
  included,
  onToggle,
  bridge,
}: {
  selection: EditorSelection;
  included: boolean;
  onToggle: () => void;
  bridge: AgentBridge;
}) {
  const lineCount = selection.toLine - selection.fromLine + 1;
  const rangeLabel =
    selection.fromLine === selection.toLine
      ? `line ${selection.fromLine}`
      : `lines ${selection.fromLine}–${selection.toLine}`;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={included}
      aria-label={
        included
          ? 'Exclude selection from next message'
          : 'Include selection in next message'
      }
      onClick={onToggle}
      title={
        included
          ? 'The agent will see this code attached to your next message. Click to exclude.'
          : 'Selection exists but is excluded from the next message. Click to include.'
      }
      className={
        'mb-2 w-full flex items-center gap-2 px-2.5 py-1.5 text-left rounded-sm border text-[10px] tracking-[0.18em] uppercase font-display transition-colors ' +
        (included
          ? 'border-yellow-300/40 bg-yellow-300/5 text-yellow-200 hover:bg-yellow-300/10'
          : 'border-zinc-800 bg-zinc-900/40 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300')
      }
    >
      <span
        aria-hidden="true"
        className={
          'h-1.5 w-1.5 rounded-full shrink-0 ' +
          (included ? 'bg-yellow-300' : 'bg-zinc-600')
        }
      />
      <span className="shrink-0">
        {lineCount} line{lineCount === 1 ? '' : 's'} selected
      </span>
      <span className="text-zinc-600 font-body normal-case tracking-normal text-[10.5px]">
        · {rangeLabel}
      </span>
      <HapsSparkline bridge={bridge} dim={!included} />
      <span className="text-[9px] text-zinc-600">
        {included ? 'include ✓' : 'excluded'}
      </span>
    </button>
  );
}

// ─── Haps sparkline ────────────────────────────────────────────────
//
// Tiny 12-column bar graph of recent hap activity, rendered to a
// canvas. Polls the bridge at 20Hz for a lightweight read of the hap
// stream; buckets the last 1.5 cycles of onsets into columns.
// Purpose is atmospheric, not informational: the chip feels alive
// while the song is playing.

const SPARK_COLS = 12;
const SPARK_LOOKBACK_CYCLES = 1.5;
const SPARK_REFRESH_MS = 50;

function HapsSparkline({
  bridge,
  dim,
}: {
  bridge: AgentBridge;
  dim: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let lastHadActivity = false;
    const draw = () => {
      const cps = bridge.getCps() ?? 0;
      const hadActivity = cps > 0;
      // Skip the redraw cost when idle and already cleared — nothing
      // is going to change until audio starts.
      if (!hadActivity && !lastHadActivity) return;
      lastHadActivity = hadActivity;
      const hapsRaw = bridge.getRecentHaps();
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      if (!cps || hapsRaw.length === 0) return;

      // Haps time is in cycles. Newest hap onset approximates "now";
      // bucket backwards from there.
      const now = hapsRaw[hapsRaw.length - 1] ?? 0;
      const start = now - SPARK_LOOKBACK_CYCLES;
      const buckets = new Array<number>(SPARK_COLS).fill(0);
      for (const t of hapsRaw) {
        if (t < start || t > now) continue;
        const frac = (t - start) / SPARK_LOOKBACK_CYCLES;
        const idx = Math.min(
          SPARK_COLS - 1,
          Math.max(0, Math.floor(frac * SPARK_COLS)),
        );
        buckets[idx]! += 1;
      }
      const peak = Math.max(1, ...buckets);
      const colW = w / SPARK_COLS;
      for (let i = 0; i < SPARK_COLS; i++) {
        const v = buckets[i]! / peak;
        if (v <= 0) continue;
        const barH = Math.max(1, Math.round(v * h));
        // Color by recency: older columns fade toward zinc, newer
        // columns push toward yellow-300.
        const recency = i / (SPARK_COLS - 1);
        const alpha = dim ? 0.25 + recency * 0.35 : 0.35 + recency * 0.65;
        ctx.fillStyle = `rgba(253, 224, 71, ${alpha})`;
        ctx.fillRect(
          Math.floor(i * colW) + 0.5,
          h - barH,
          Math.max(1, Math.floor(colW) - 1),
          barH,
        );
      }
    };

    draw();
    const handle = window.setInterval(draw, SPARK_REFRESH_MS);
    return () => window.clearInterval(handle);
  }, [bridge, dim]);

  return (
    <canvas
      ref={canvasRef}
      width={48}
      height={12}
      className="ml-auto mr-1 shrink-0 opacity-90"
      aria-hidden="true"
      title="Recent hap activity"
    />
  );
}

function Dropdown({
  ariaLabel,
  value,
  disabled,
  onChange,
  options,
}: {
  ariaLabel: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <span className="relative">
      <select
        aria-label={ariaLabel}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={
          'appearance-none bg-zinc-900 border border-zinc-800 ' +
          'text-[9px] tracking-[0.22em] uppercase font-display ' +
          'text-zinc-300 pr-4 pl-1.5 py-0.5 rounded-sm ' +
          'hover:border-yellow-300/40 hover:text-yellow-300 ' +
          'focus:outline-none focus:border-yellow-300/60 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'cursor-pointer transition-colors'
        }
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-zinc-600"
      >
        ▾
      </span>
    </span>
  );
}

function TurnBlock({
  turn,
  isLast,
  onDecision,
  onRevert,
}: {
  turn: Turn;
  isLast: boolean;
  onDecision: (useId: string, decision: 'apply' | 'skip') => void;
  onRevert: () => void;
}) {
  const canRevert =
    isLast &&
    turn.snapshot !== null &&
    turn.hasAppliedWrite &&
    !turn.reverted &&
    turn.status !== 'streaming';
  const pulseMs = turn.beatMs != null ? Math.max(120, turn.beatMs) : 1200;
  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-start">
        <Avatar role="user" />
        <div className="flex-1 font-body text-[12.5px] text-zinc-200 whitespace-pre-wrap leading-[1.5]">
          {turn.user}
        </div>
        {canRevert && (
          <button
            type="button"
            onClick={onRevert}
            title="Undo every edit applied in this turn"
            className="shrink-0 px-2 py-0.5 text-[9px] tracking-[0.22em] uppercase font-display rounded-sm border border-rose-400/40 bg-zinc-900 text-rose-300 hover:bg-rose-400/10"
          >
            Revert
          </button>
        )}
        {turn.reverted && (
          <span className="shrink-0 px-2 py-0.5 text-[9px] tracking-[0.22em] uppercase font-display rounded-sm border border-zinc-700 text-zinc-500">
            reverted
          </span>
        )}
      </div>

      {turn.toolCalls.map((t) => (
        <ToolCallRow key={t.id} trace={t} />
      ))}

      {turn.pending.map((p) => (
        <PendingRow
          key={p.id}
          pending={p}
          onKeep={() => onDecision(p.id, 'apply')}
          onKill={() => onDecision(p.id, 'skip')}
        />
      ))}

      {(turn.assistant || turn.status === 'streaming') && (
        <div className="flex gap-2">
          <Avatar role="assistant" />
          <div className="flex-1 font-body text-[12.5px] text-zinc-100 whitespace-pre-wrap leading-[1.55]">
            {turn.assistant}
            {turn.status === 'streaming' && (
              <span
                aria-hidden="true"
                style={{ animationDuration: `${pulseMs}ms` }}
                className="inline-block w-1.5 h-3 ml-0.5 bg-yellow-300/70 align-middle animate-pulse"
              />
            )}
          </div>
        </div>
      )}

      {turn.status === 'error' && (
        <div className="ml-8 text-[11px] text-rose-300 font-body">
          Error: {turn.error ?? 'unknown'}
        </div>
      )}
      {turn.status === 'aborted' && (
        <div className="ml-8 text-[11px] text-amber-300 font-body">
          Cancelled.
        </div>
      )}
      {turn.usage && <UsageReadout usage={turn.usage} model={turn.model} />}
    </div>
  );
}

function UsageReadout({
  usage,
  model,
}: {
  usage: UsageSummary;
  model: ModelId;
}) {
  const totalInput =
    usage.inputTokens + usage.cacheReadTokens + usage.cacheWriteTokens;
  const cachedPct =
    totalInput > 0
      ? Math.round((usage.cacheReadTokens / totalInput) * 100)
      : 0;
  const dollars = costOf(model, usage);
  const modelLabel =
    MODEL_OPTIONS.find((o) => o.id === model)?.label ?? model;
  return (
    <div className="ml-8 flex items-center gap-3 text-[9.5px] tracking-[0.2em] uppercase font-display text-zinc-600">
      <span title="Total input tokens across the tool-use loop">
        in {fmtTokens(totalInput)}
      </span>
      <span className="text-zinc-800">·</span>
      <span title="Output tokens generated">
        out {fmtTokens(usage.outputTokens)}
      </span>
      <span className="text-zinc-800">·</span>
      <span
        title={`Served from cache: ${usage.cacheReadTokens} of ${totalInput} input tokens`}
        className={cachedPct >= 50 ? 'text-emerald-500/70' : ''}
      >
        ⟲ {cachedPct}%
      </span>
      <span className="text-zinc-800">·</span>
      <span
        title={`Estimated cost on ${modelLabel}. Cache reads billed at 0.1× input rate; cache writes at 1.25×.`}
        className="text-zinc-400"
      >
        {formatCost(dollars)}
      </span>
    </div>
  );
}

function fmtTokens(n: number): string {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return k >= 10 ? `${Math.round(k)}K` : `${k.toFixed(1)}K`;
}

function Avatar({ role }: { role: 'user' | 'assistant' }) {
  const label = role === 'user' ? 'you' : 'agent';
  const tone =
    role === 'user'
      ? 'border-zinc-700 text-zinc-400 bg-zinc-900'
      : 'border-yellow-300/40 text-yellow-300 bg-zinc-950';
  return (
    <span
      aria-hidden="true"
      className={
        'shrink-0 h-6 w-6 rounded-sm border grid place-items-center text-[8px] tracking-[0.18em] uppercase font-display ' +
        tone
      }
    >
      {label}
    </span>
  );
}

function ToolCallRow({ trace }: { trace: ToolCallTrace }) {
  const [expanded, setExpanded] = useState(false);
  const style = toolStyleFor(trace.name);
  const appliedMutation =
    trace.status === 'applied' && trace.mutatesSong;

  // Killed writes collapse to a single terse line — musical decision,
  // not an error. Don't clutter the log with an expandable card.
  if (trace.status === 'skipped') {
    return (
      <div className="ml-8 px-2.5 py-1 text-[10.5px] font-display tracking-[0.18em] uppercase text-zinc-600 flex items-center gap-2">
        <span aria-hidden="true" className="text-zinc-700">
          ✕
        </span>
        <span>kill —</span>
        <span className="font-mono normal-case tracking-normal text-zinc-500">
          {trace.name}
        </span>
      </div>
    );
  }

  const flashRef = useFlashOnMount(appliedMutation);
  const dotClass =
    trace.status === 'failed' ? 'bg-rose-400' : style.accent;
  const statusLabel = trace.status === 'failed' ? 'failed' : 'applied';

  return (
    <div
      ref={flashRef}
      className="ml-8 rounded-sm border border-zinc-800 bg-zinc-900/50 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-zinc-900/80"
      >
        <span
          aria-hidden="true"
          className={'w-4 text-center ' + style.accent}
        >
          {style.glyph}
        </span>
        <span
          aria-hidden="true"
          className={
            'h-1.5 w-1.5 rounded-full ' +
            (dotClass.startsWith('text-')
              ? dotClass.replace('text-', 'bg-')
              : dotClass)
          }
        />
        <span className="font-mono text-[10.5px] text-zinc-300">
          {trace.name}
        </span>
        <span className="font-mono text-[10px] text-zinc-600 truncate flex-1">
          {summarizeInput(trace.input)}
        </span>
        <span className="text-[9px] tracking-[0.2em] uppercase font-display text-zinc-600">
          {statusLabel} · {expanded ? 'hide' : 'show'}
        </span>
      </button>
      {expanded && (
        <div className="px-2.5 py-2 border-t border-zinc-900 bg-zinc-950/60 space-y-1.5">
          <ToolSection label="input" value={trace.input} />
          <ToolSection label="result" value={trace.result} />
        </div>
      )}
    </div>
  );
}

// Attach the `.tool-flash` class on mount when `shouldFlash` is true.
// Uses a ref so we can set a CSS variable for the flash color before
// the browser paints the first frame.
function useFlashOnMount(shouldFlash: boolean): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!shouldFlash) return;
    const el = ref.current;
    if (!el) return;
    el.classList.add('tool-flash');
    const handle = window.setTimeout(() => {
      el.classList.remove('tool-flash');
    }, 700);
    return () => window.clearTimeout(handle);
    // shouldFlash is read once at mount — don't re-run on unrelated
    // ancestor re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

function PendingRow({
  pending,
  onKeep,
  onKill,
}: {
  pending: PendingToolCall;
  onKeep: () => void;
  onKill: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const style = toolStyleFor(pending.name);
  return (
    <div className="ml-8 rounded-sm border border-yellow-300/40 bg-yellow-300/5 overflow-hidden">
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <span
          aria-hidden="true"
          className={'w-4 text-center ' + style.accent}
        >
          {style.glyph}
        </span>
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full bg-yellow-300 shadow-[0_0_8px_rgba(253,224,71,0.9)] animate-pulse"
        />
        <span className="font-mono text-[10.5px] text-yellow-200">
          {pending.name}
        </span>
        <span className="font-mono text-[10px] text-zinc-400 truncate flex-1">
          {summarizeInput(pending.input)}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[9px] tracking-[0.2em] uppercase font-display text-zinc-500 hover:text-yellow-300"
        >
          {expanded ? 'hide' : 'show'}
        </button>
      </div>
      {expanded && (
        <div className="px-2.5 py-2 border-t border-yellow-300/20 bg-zinc-950/40">
          <ToolSection label="proposed input" value={pending.input} />
        </div>
      )}
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-t border-yellow-300/20 bg-zinc-950/30">
        <span className="text-[10px] text-zinc-500 font-body flex-1">
          Keep runs the edit; Kill tells the agent you don&apos;t want it.
        </span>
        <button
          type="button"
          onClick={onKill}
          className="px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-display rounded-sm border border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 line-through decoration-zinc-600 decoration-1 hover:decoration-zinc-400"
        >
          Kill
        </button>
        <button
          type="button"
          onClick={onKeep}
          className="px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-display rounded-sm border border-yellow-300 bg-yellow-300 text-zinc-950 hover:brightness-95"
        >
          Keep
        </button>
      </div>
    </div>
  );
}

function ToolSection({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="text-[8.5px] tracking-[0.28em] uppercase font-display text-zinc-600 mb-0.5">
        {label}
      </div>
      <pre className="font-mono text-[10.5px] text-zinc-300 leading-[1.4] whitespace-pre-wrap break-all max-h-48 overflow-y-auto scrollbar-thin">
        {safeStringify(value)}
      </pre>
    </div>
  );
}

function composeMessage(
  userText: string,
  selection: EditorSelection | null,
): string {
  if (!selection) return userText;
  // Escape the closing tag if it happens to appear in the selection
  // — very unlikely in Strudel code, but an unguarded selection that
  // contains "</selected-code>" would break the boundary and cause
  // the model to misread the user's prose.
  const safeText = selection.text.replace(
    /<\/selected-code>/g,
    '<\\/selected-code>',
  );
  return [
    `<selected-code lines="${selection.fromLine}-${selection.toLine}">`,
    safeText,
    '</selected-code>',
    '',
    userText,
  ].join('\n');
}

function safeGetSong(bridge: AgentBridge): Song | null {
  try {
    const s = bridge.getSong();
    return s ?? null;
  } catch {
    return null;
  }
}

function cloneStatements(statements: Song['statements']): Song['statements'] {
  return JSON.parse(JSON.stringify(statements)) as Song['statements'];
}

function summarizeInput(input: unknown): string {
  if (input === null || input === undefined) return '';
  if (typeof input !== 'object') return String(input);
  const entries = Object.entries(input as Record<string, unknown>);
  if (entries.length === 0) return '()';
  return entries.map(([k, v]) => `${k}: ${shortValue(v)}`).join(', ');
}

function shortValue(v: unknown): string {
  if (typeof v === 'string')
    return v.length > 24 ? `"${v.slice(0, 22)}…"` : `"${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '…';
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function EmptyState() {
  return (
    <div className="py-6 text-center">
      <div className="font-display text-[10px] tracking-[0.32em] uppercase text-zinc-600 mb-2">
        idle
      </div>
      <p className="text-[11.5px] text-zinc-500 leading-[1.55] font-body max-w-[32ch] mx-auto">
        Ask anything, or type <span className="font-mono text-yellow-300">/</span>{' '}
        for seeds like <em>/swap bass</em>, <em>/double time</em>,{' '}
        <em>/break it down</em>.
      </p>
    </div>
  );
}

function KeyMissingState({
  onOpenSettings,
}: {
  onOpenSettings: () => void;
}) {
  return (
    <div className="py-6 text-center space-y-3">
      <div className="font-display text-[10px] tracking-[0.32em] uppercase text-yellow-300">
        api key required
      </div>
      <p className="text-[11.5px] text-zinc-400 leading-[1.55] font-body max-w-[34ch] mx-auto">
        The agent calls Claude directly from your browser. Paste an
        Anthropic API key in Settings — it stays in localStorage and is
        never sent anywhere except Anthropic.
      </p>
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={onOpenSettings}
          className="px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-display rounded-sm border border-yellow-300/50 bg-zinc-900 text-yellow-300 hover:bg-yellow-300/10"
        >
          Open settings
        </button>
        <a
          href="https://console.anthropic.com/settings/keys"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10.5px] tracking-[0.18em] uppercase text-zinc-500 hover:text-zinc-300 font-display"
        >
          get a key ↗
        </a>
      </div>
    </div>
  );
}
